import React, { createContext, useContext, useState, useEffect } from 'react';
import { CognitoUser, AuthenticationDetails, CognitoUserPool } from 'amazon-cognito-identity-js';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children, config }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPool, setUserPool] = useState(null);

  useEffect(() => {
    if (config) {
      const pool = new CognitoUserPool({
        UserPoolId: config.userPoolId,
        ClientId: config.userPoolClientId,
      });
      setUserPool(pool);
      
      const currentUser = pool.getCurrentUser();
      if (currentUser) {
        currentUser.getSession((err, session) => {
          if (err || !session.isValid()) {
            setLoading(false);
            return;
          }
          
          currentUser.getUserAttributes((err, attributes) => {
            if (!err) {
              const userData = attributes.reduce((acc, attr) => {
                acc[attr.Name] = attr.Value;
                return acc;
              }, {});
              
              const groups = session.getIdToken().payload['cognito:groups'] || [];
              setUser({ ...userData, groups, username: currentUser.getUsername(), userPool: pool });
            }
            setLoading(false);
          });
        });
      } else {
        setLoading(false);
      }
    }
  }, [config]);

  const signIn = (email, password) => {
    return new Promise((resolve, reject) => {
      const authDetails = new AuthenticationDetails({
        Username: email,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              reject(err);
              return;
            }
            const userData = attributes.reduce((acc, attr) => {
              acc[attr.Name] = attr.Value;
              return acc;
            }, {});
            const groups = session.getIdToken().payload['cognito:groups'] || [];
            setUser({ ...userData, groups, username: cognitoUser.getUsername(), userPool });
            resolve(session);
          });
        },
        onFailure: reject,
        newPasswordRequired: (userAttributes) => {
          reject({ code: 'NewPasswordRequired', cognitoUser });
        },
      });
    });
  };

  const completeNewPassword = (cognitoUser, newPassword) => {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
        onSuccess: (session) => {
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) { reject(err); return; }
            const userData = attributes.reduce((acc, attr) => { acc[attr.Name] = attr.Value; return acc; }, {});
            const groups = session.getIdToken().payload['cognito:groups'] || [];
            setUser({ ...userData, groups, username: cognitoUser.getUsername(), userPool });
            resolve(session);
          });
        },
        onFailure: reject,
      });
    });
  };

  const signUp = (email, password, attributes = {}) => {
    return new Promise((resolve, reject) => {
      const attributeList = Object.entries(attributes).map(([key, value]) => ({
        Name: key,
        Value: value,
      }));

      userPool.signUp(email, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const signOut = () => {
    if (userPool) {
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.signOut();
      }
    }
    setUser(null);
  };

  const confirmSignUp = (email, code) => {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  };

  const isAdmin = () => user?.groups?.includes('admin');
  const isStreamer = () => user?.groups?.includes('streamer') || isAdmin();

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      signIn,
      signUp,
      signOut,
      confirmSignUp,
      completeNewPassword,
      isAdmin,
      isStreamer,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
